import axiosInstance from './http'
import authHeader from './authHeader'

const FLUSH_INTERVAL_MS = 5000
const MAX_QUEUE_SIZE = 20
const MAX_STRING_LENGTH = 2000

let queue = []
let flushTimer = null
let isLoggingInternally = false

const isAuthenticated = () => !!localStorage.getItem('bolashak_token')

const safeStringifyArgs = args => {
	try {
		return args
			.map(arg => {
				if (arg instanceof Error) {
					return { message: arg.message, stack: arg.stack }
				}
				if (typeof arg === 'string') {
					return arg.slice(0, MAX_STRING_LENGTH)
				}
				const json = JSON.stringify(arg)
				return json ? json.slice(0, MAX_STRING_LENGTH) : String(arg).slice(0, MAX_STRING_LENGTH)
			})
			.slice(0, 10)
	} catch (e) {
		return ['[unserializable console arguments]']
	}
}

const scheduleFlush = () => {
	if (flushTimer) return
	flushTimer = setTimeout(() => {
		flushTimer = null
		flush()
	}, FLUSH_INTERVAL_MS)
}

const flush = (useKeepalive = false) => {
	if (!queue.length || !isAuthenticated()) return
	const events = queue
	queue = []

	if (useKeepalive) {
		try {
			fetch(`${axiosInstance.defaults.baseURL}system-logs`, {
				method: 'POST',
				keepalive: true,
				headers: authHeader(),
				body: JSON.stringify({ events }),
			}).catch(() => {})
		} catch (e) {
			// best-effort only
		}
		return
	}

	axiosInstance
		.post('system-logs', { events }, { headers: authHeader() })
		.catch(() => {
			// Logging failures must never surface to the user or retry endlessly.
		})
}

export const logEvent = (category, action, { message = null, data = null, level = 'info' } = {}) => {
	try {
		queue.push({ category, action, message, data, level })
		if (queue.length >= MAX_QUEUE_SIZE) {
			flush()
		} else {
			scheduleFlush()
		}
	} catch (e) {
		// never throw from logging
	}
}

const patchConsole = () => {
	;['error', 'warn'].forEach(method => {
		const original = console[method]
		console[method] = (...args) => {
			original(...args)
			if (isLoggingInternally) return
			isLoggingInternally = true
			try {
				logEvent('error', `console-${method}`, {
					level: method === 'error' ? 'error' : 'warning',
					data: { args: safeStringifyArgs(args) },
				})
			} finally {
				isLoggingInternally = false
			}
		}
	})
}

let initialized = false

export const initSystemLogger = () => {
	if (initialized) return
	initialized = true

	patchConsole()

	window.addEventListener('error', event => {
		logEvent('error', 'window-error', {
			level: 'error',
			message: event?.message,
			data: {
				filename: event?.filename,
				lineno: event?.lineno,
				colno: event?.colno,
				stack: event?.error?.stack,
			},
		})
	})

	window.addEventListener('unhandledrejection', event => {
		const reason = event?.reason
		logEvent('error', 'unhandled-rejection', {
			level: 'error',
			message: reason instanceof Error ? reason.message : String(reason),
			data: { stack: reason instanceof Error ? reason.stack : undefined },
		})
	})

	const flushOnUnload = () => flush(true)
	window.addEventListener('beforeunload', flushOnUnload)
	window.addEventListener('pagehide', flushOnUnload)
}

const SystemLogger = { logEvent, initSystemLogger }
export default SystemLogger
